// src/services/achievementLinksService.ts
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';

export interface AchievementLink {
  id: number;
  achievementId: number;
  url: string;
  title: string;
  type: 'youtube' | 'website';
  userId?: number;
  createdAt?: string;
}

export interface NewAchievementLink {
  achievementId: number;
  url: string;
  title: string;
  type: 'youtube' | 'website';
}

/**
 * Get all links for a specific achievement
 */
export const getAchievementLinks = async (achievementId: number): Promise<AchievementLink[]> => {
  try {
    const response = await axios.get(
      `${API_CONFIG.BASE_URL}/achievement-links?achievement_id=${achievementId}`
    );
    
    if (response.data.status === 'success') {
      return response.data.data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching achievement links:', error);
    return [];
  }
};

/**
 * Add a new link to an achievement
 */
export const addAchievementLink = async (linkData: NewAchievementLink): Promise<AchievementLink | null> => {
  try {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}/achievement-links`,
      linkData
    );
    
    if (response.data.status === 'success') {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to add link');
  } catch (error: any) {
    console.error('Error adding achievement link:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to add link');
  }
};

/**
 * Delete an achievement link
 */
export const deleteAchievementLink = async (linkId: number): Promise<boolean> => {
  try {
    const response = await axios.delete(
      `${API_CONFIG.BASE_URL}/achievement-links/${linkId}`
    );
    
    return response.data.status === 'success';
  } catch (error) {
    console.error('Error deleting achievement link:', error);
    throw new Error('Failed to delete link');
  }
};

/**
 * Update an existing achievement link
 */
export const updateAchievementLink = async (
  linkId: number,
  linkData: Partial<NewAchievementLink>
): Promise<AchievementLink | null> => {
  try {
    const response = await axios.put(
      `${API_CONFIG.BASE_URL}/achievement-links/${linkId}`,
      linkData
    );
    
    if (response.data.status === 'success') {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Error updating achievement link:', error);
    throw new Error('Failed to update link');
  }
};